import { Component, inject, OnInit, QueryList, ViewChildren, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../Services/auth.service';
import { NavbarService } from '../../../Services/navbar.service';
import { FirmSelectionService } from '../../../Services/firm-selection.service';
import { NotificationService } from '../../../Services/notification.service';
import { jwtDecode } from 'jwt-decode';

@Component({
  selector: 'app-validate-otp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './validate-otp.component.html',
  styleUrls: ['./validate-otp.component.css'],
})
export class ValidateOtpComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private navbarService = inject(NavbarService);
  private firmSelectionService = inject(FirmSelectionService);
  private notificationService = inject(NotificationService);

  otpForm!: FormGroup;
  mobilenumber!: string;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  userType: string = '';
  ownerEmail: string = '';
  isLoading: boolean = false;

  ngOnInit() {
    const nav = history.state;
    this.userType = nav?.user_type || '';
    this.ownerEmail = nav?.owner_email || '';
    const passedMobile = nav?.mobilenumber || '';

    this.otpForm = this.fb.group({
      mobilenumber: [passedMobile, [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      d1: ['', [Validators.required, Validators.pattern('[0-9]')]],
      d2: ['', [Validators.required, Validators.pattern('[0-9]')]],
      d3: ['', [Validators.required, Validators.pattern('[0-9]')]],
      d4: ['', [Validators.required, Validators.pattern('[0-9]')]],
      d5: ['', [Validators.required, Validators.pattern('[0-9]')]],
      d6: ['', [Validators.required, Validators.pattern('[0-9]')]],
    });
  }

  ngAfterViewInit() {
    this.otpInputs.first.nativeElement.focus();
  }

  handleOtpInput(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && index < this.otpInputs.length - 1) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }

    if (!value && event.key === 'Backspace' && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    }

    const key = `d${index + 1}`;
    this.otpForm.get(key)?.setValue(value);
  }

  verifyOtp() {
    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const otp = Object.values(this.otpForm.value)
      .filter((v, i) => i !== 0)
      .join('');

    const payload = {
      user_type: this.userType,
      owner_email: this.ownerEmail,
      mobilenumber: this.otpForm.get('mobilenumber')?.value,
      otp: otp
    };

    this.authService.verifyOtpLogin(payload).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.success && response.token) {
          sessionStorage.setItem('token', response.token);
          if (response.user) {
            sessionStorage.setItem('user', JSON.stringify(response.user));
          }

          const decoded: any = jwtDecode(response.token);
          sessionStorage.setItem('role', decoded.role ?? '');

          const userRole = decoded.role;
          const userSubRole = decoded.sub_role;

          this.navbarService.updateSettingsVisibility();

          if (response.firm_id) {
            localStorage.setItem('firm_id', response.firm_id);
            sessionStorage.setItem('is_firm_restricted', 'true');

            if (response.firm_name && response.gst_status) {
              this.firmSelectionService.setselectedFirmName({
                id: response.firm_id,
                name: response.firm_name,
                status: response.gst_status
              });
            }
          } else {
            sessionStorage.removeItem('is_firm_restricted');
          }

          this.notificationService.showSuccess('OTP Verified Successfully!', 'Success');

          if (userRole === 'Owner') {
            const isGst = response.gst_status && this.firmSelectionService.isGstFirm(response.gst_status, response.firm_name);
            if (isGst) {
              this.router.navigate(['/invoice-gst-panel']);
            } else {
              this.router.navigate(['/home']);
            }
          } else if (userRole === 'Staff') {
            if (userSubRole === 'Karigar') {
              localStorage.removeItem('firm_id');
              sessionStorage.removeItem('is_firm_restricted');
              this.router.navigate(['/kalakaar-management']);
            } else if (userSubRole === 'Chartered Accountant') {
              this.router.navigate(['/invoice-gst-panel']);
            } else if (userSubRole === 'Customer') {
              this.router.navigate(['/customer-management']);
            } else {
              const isGst = response.gst_status && this.firmSelectionService.isGstFirm(response.gst_status, response.firm_name);
              if (isGst) {
                this.router.navigate(['/invoice-gst-panel']);
              } else {
                this.router.navigate(['/rm-portal/home']);
              }
            }
          } else {
            this.router.navigate(['/home']);
          }
        } else {
          this.notificationService.showError(response.message || 'Invalid OTP', 'Error');
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error('Error:', err);
        this.notificationService.showError(err.error?.message || 'Something went wrong, please try again.', 'Error');
      }
    });
  }

  resendOtp() {
    const mobile = this.otpForm.get('mobilenumber')?.value;
    if (!mobile) return;

    this.authService.sendOtpLogin({
      user_type: this.userType,
      owner_email: this.ownerEmail,
      mobilenumber: mobile
    }).subscribe({
      next: (res: any) => this.notificationService.showSuccess(res.message || 'OTP Resent!', 'Success'),
      error: (err: any) => this.notificationService.showError(err.error?.message || 'Failed to resend OTP', 'Error')
    });
  }
}


